import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatHours, type AttendanceDay, type AttendanceMonth, type DayStatus, netDeficitSeconds } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { useAttendance, useUsers } from '@/hooks/queries';
import { Card, ErrorNote, PageHeader, Spinner, StatTile } from '@/components/ui';
import { formatTime } from '@/lib/format';
import { DayEntriesModal } from '@/components/DayEntriesModal';
import { LeaveBankCard } from '@/components/LeaveBankCard';

const pad = (n: number) => String(n).padStart(2, '0');

export function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Shared month stepper used by both attendance pages. */
export function MonthNav({ month, onChange }: { month: string; onChange: (m: string) => void }) {
  const isCurrent = month === currentMonthKey();
  return (
    <div className="flex items-center gap-1">
      <button className="btn-secondary" onClick={() => onChange(shiftMonth(month, -1))} aria-label="Previous month">←</button>
      <span className="min-w-[10rem] text-center text-sm font-medium text-slate-700">{monthLabel(month)}</span>
      <button className="btn-secondary" onClick={() => onChange(shiftMonth(month, 1))} disabled={isCurrent} aria-label="Next month">→</button>
      {!isCurrent && <button className="btn-secondary" onClick={() => onChange(currentMonthKey())}>This month</button>}
    </div>
  );
}

const STATUS_STYLE: Record<DayStatus, { bg: string; text: string; label: string }> = {
  present: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', label: 'Present' },
  half: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', label: 'Half day' },
  leave: { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-800', label: 'Leave' },
  'in-progress': { bg: 'bg-brand-50 border-brand-200', text: 'text-brand-800', label: 'Today' },
  upcoming: { bg: 'bg-white border-slate-200', text: 'text-slate-400', label: '' },
  off: { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-500', label: 'Off' },
};

export function AttendancePage() {
  const { user, hasRole, breakProject } = useAuth();
  const [editDay, setEditDay] = useState<AttendanceDay | null>(null);
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? currentMonthKey();
  const canPickUser = hasRole('ADMIN', 'MANAGER');
  const userId = (canPickUser && params.get('userId')) || undefined;

  const users = useUsers({ status: 'ACTIVE' });
  const { data, isLoading, error } = useAttendance(month, userId);

  const setMonth = (m: string) => setParams((p) => { p.set('month', m); return p; });
  const setUser = (id: string) => setParams((p) => { if (id) p.set('userId', id); else p.delete('userId'); return p; });

  const viewingSelf = !userId || userId === user?.id;
  // Admins correct attendance by logging the hours that were actually worked.
  const canEdit = hasRole('ADMIN');

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={viewingSelf ? 'My attendance' : `${data?.userName ?? '…'} — attendance`}
        description="Working days, hours against the expected 8h, and any leaves, half days or late arrivals."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canPickUser && (
              <select className="input max-w-[14rem]" value={userId ?? ''} onChange={(e) => setUser(e.target.value)}>
                <option value="">Me</option>
                {users.data?.items.filter((u) => u.id !== user?.id).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            )}
            <MonthNav month={month} onChange={setMonth} />
          </div>
        }
      />

      <ErrorNote error={error} />

      {isLoading || !data ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <>
          <Totals data={data} />
          <LeaveBankCard month={month} userId={userId} />
          <Calendar data={data} onSelect={canEdit ? setEditDay : undefined} />
          <Legend editable={canEdit} />
          {editDay && (
            <DayEntriesModal
              key={editDay.date}
              userId={data.userId}
              userName={data.userName}
              day={data.days.find((d) => d.date === editDay.date) ?? editDay}
              timezone={data.timezone}
              breakProject={breakProject}
              onClose={() => setEditDay(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function Totals({ data }: { data: AttendanceMonth }) {
  const t = data.totals;
  // Deficit that survives offsetting, not expected-minus-worked: the latter
  // counts absent days that are already charged as leave.
  const shortfall = netDeficitSeconds(t);
  const surplus = Math.max(0, (t.surplusSeconds ?? 0) - (t.deficitSeconds ?? 0));
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-7">
      <StatTile label="Hours worked" value={formatHours(t.workSeconds)} hint={`of ${formatHours(t.expectedSeconds)} expected`} tone="brand" />
      <StatTile
        label="Breaks"
        value={formatHours(t.breakSeconds)}
        hint={t.noBreakDays > 0 ? `${t.noBreakDays} worked day${t.noBreakDays === 1 ? '' : 's'} with no break` : `of ${formatHours(t.expectedBreakSeconds)} expected`}
        tone={t.noBreakDays > 0 ? 'default' : 'green'}
      />
      <StatTile
        label={shortfall > 0 ? 'Short hours' : 'Extra hours'}
        value={formatHours(shortfall > 0 ? shortfall : surplus)}
        hint={
          shortfall > 0
            ? 'Short hours on full days, after extra hours offset them'
            : `${t.elapsedWorkingDays} of ${t.workingDays} working days so far`
        }
        tone={shortfall > 0 ? 'rose' : 'green'}
      />
      <StatTile label="Present" value={String(t.presentDays)} hint="full days" tone="green" />
      <StatTile label="Half days" value={String(t.halfDays)} />
      <StatTile label="Leaves" value={String(t.leaveDays)} hint="working days with no work" />
      <StatTile label="Late" value={String(t.lateDays)} hint="arrived after grace" />
    </div>
  );
}

function Calendar({ data, onSelect }: { data: AttendanceMonth; onSelect?: (day: AttendanceDay) => void }) {
  const weeks = useMemo(() => {
    // Monday-first grid; pad the first row with blanks.
    const [y, m] = data.month.split('-').map(Number);
    const firstWeekday = new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); // 0=Sun
    const lead = (firstWeekday + 6) % 7;
    const cells: (AttendanceDay | null)[] = [...Array<null>(lead).fill(null), ...data.days];
    while (cells.length % 7) cells.push(null);
    const rows: (AttendanceDay | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [data]);

  return (
    <Card className="mt-4 overflow-hidden">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">{d}</div>
        ))}
      </div>
      {weeks.map((row, i) => (
        <div key={i} className="grid grid-cols-7 divide-x divide-slate-100 border-b border-slate-100 last:border-b-0">
          {row.map((day, j) => (day ? <DayCell key={day.date} day={day} onSelect={onSelect} /> : <div key={`b${j}`} className="min-h-[5.5rem] bg-slate-50/50" />))}
        </div>
      ))}
    </Card>
  );
}

function DayCell({ day, onSelect }: { day: AttendanceDay; onSelect?: (day: AttendanceDay) => void }) {
  const style = STATUS_STYLE[day.status];
  // A full day that came up short still counts as present, so the status colour
  // stays; the pink wash is what makes the shortfall noticeable.
  const short = (day.deficitSeconds ?? 0) > 0;
  const dayNum = Number(day.date.slice(-2));
  const working = day.status !== 'off';
  // Future days cannot have hours logged; everything else can be corrected.
  const clickable = !!onSelect && day.status !== 'upcoming';
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? () => onSelect!(day) : undefined}
      className={`block min-h-[5.5rem] w-full border-l-0 p-1.5 text-left ${short ? 'border-pink-300 bg-pink-100' : style.bg} ${clickable ? 'cursor-pointer transition hover:ring-2 hover:ring-inset hover:ring-brand-400' : ''}`}
      title={clickable ? `${day.label ? `${day.label} · ` : ''}Click to view or log hours` : (day.label ?? undefined)}
    >
      <div className="flex items-start justify-between">
        <span className={`text-sm font-medium ${working ? 'text-slate-800' : 'text-slate-400'}`}>{dayNum}</span>
        <div className="flex flex-col items-end gap-0.5">
          {day.leave && (
            <span
              className={`rounded px-1 text-[10px] font-semibold ${
                day.leave.status === 'APPROVED' ? 'bg-emerald-600 text-white' : day.leave.status === 'PENDING' ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-700 line-through'
              }`}
              title={`${day.leave.type === 'MEDICAL' ? 'Medical' : 'Casual'} leave · ${day.leave.status.toLowerCase()}${day.leave.halfDay ? ' · half day' : ''}`}
            >
              {day.leave.type === 'MEDICAL' ? 'ML' : 'CL'}{day.leave.halfDay ? '½' : ''}
            </span>
          )}
          {day.late && <span className="rounded bg-rose-600 px-1 text-[10px] font-semibold text-white">LATE</span>}
          {style.label && day.status !== 'upcoming' && <span className={`text-[10px] font-semibold uppercase ${style.text}`}>{style.label}</span>}
        </div>
      </div>

      {day.label && <p className="mt-0.5 truncate text-[11px] text-slate-600" title={day.label}>{day.kind === 'holiday' ? '🎉 ' : '💼 '}{day.label}</p>}

      {(day.workSeconds > 0 || day.breakSeconds > 0) && (
        <div className="mt-1 space-y-0.5">
          <p className="font-mono text-sm font-semibold tabular-nums text-slate-800">{formatHours(day.workSeconds)}</p>
          {short && (
            <p className="text-[11px] font-semibold text-pink-700" title="Short of the expected day. Counted toward the monthly deficit, not as a half day.">
              −{formatHours(day.deficitSeconds)} short
            </p>
          )}
          {day.breakSeconds > 0 ? (
            <p className="text-[11px] text-slate-500">☕ {formatHours(day.breakSeconds)}</p>
          ) : (day.status === 'present' || day.status === 'half') ? (
            <p className="text-[11px] font-medium text-amber-700" title="No break logged — was the whole day put on a project?">☕ no break</p>
          ) : null}
          {day.firstIn && (
            <p className="text-[11px] tabular-nums text-slate-500">
              {formatTime(day.firstIn)}{day.lastOut ? ` – ${formatTime(day.lastOut)}` : ''}
            </p>
          )}
        </div>
      )}
    </Tag>
  );
}

function Legend({ editable }: { editable: boolean }) {
  const items: DayStatus[] = ['present', 'half', 'leave', 'in-progress', 'off'];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
      {items.map((s) => (
        <span key={s} className="flex items-center gap-1.5">
          <span className={`inline-block h-3 w-3 rounded border ${STATUS_STYLE[s].bg}`} />
          {STATUS_STYLE[s].label}
        </span>
      ))}
      <span className="flex items-center gap-1.5"><span className="rounded bg-rose-600 px-1 text-[10px] font-semibold text-white">LATE</span> after start + grace</span>
      <span className="flex items-center gap-1.5"><span className="rounded bg-emerald-600 px-1 text-[10px] font-semibold text-white">CL/ML</span> approved leave</span>
      <span className="flex items-center gap-1.5"><span className="rounded bg-amber-500 px-1 text-[10px] font-semibold text-white">CL</span> awaiting approval</span>
      <span className="flex items-center gap-1.5"><span className="text-amber-700">☕ no break</span> worked day with no break logged</span>
      <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded border border-pink-300 bg-pink-100" /> short of the full day</span>
      <span className="ml-auto">
        {editable ? 'Click any past day to view or log hours. ' : ''}Hours exclude breaks. Days reckoned in the attendance timezone set by your admin.
      </span>
    </div>
  );
}
